import { Category, MetadataStatus } from "../../model/Category";

const MAX_DEPTH = 64;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asLocaleMap = (value: unknown): Record<string, string> => {
  if (!isObject(value)) return {};
  const result: Record<string, string> = {};
  for (const [locale, text] of Object.entries(value)) {
    if (typeof text === "string") {
      result[locale] = text;
    }
  }
  return result;
};

const asStatus = (value: unknown): MetadataStatus | null => {
  if (typeof value !== "string") return null;
  const normalized = value.toUpperCase();
  return (Object.values(MetadataStatus) as string[]).includes(normalized)
    ? (normalized as MetadataStatus)
    : null;
};

const asId = (value: unknown): number | null =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0
    ? value
    : null;

const countSubtree = (value: unknown, depth = 0): number => {
  if (!Array.isArray(value) || depth > MAX_DEPTH) return 0;
  let count = 0;
  for (const child of value) {
    count += 1;
    if (isObject(child)) {
      count += countSubtree(child.children, depth + 1);
    }
  }
  return count;
};

export interface NormalizedCategories {
  categories: Category[];
  valid: boolean;
  dropped: number;
}

/**
 * Normalises the category tree at the trust boundary: the admin UI must never
 * crash on a malformed payload (wrong types, duplicate ids, absurd nesting).
 */
export const normalizeCategories = (input: unknown): NormalizedCategories => {
  if (!Array.isArray(input)) {
    return { categories: [], valid: false, dropped: 0 };
  }

  const seen = new Set<number>();
  let dropped = 0;

  const walk = (nodes: unknown[], depth: number): Category[] => {
    if (depth > MAX_DEPTH) {
      dropped += nodes.length;
      return [];
    }

    const result: Category[] = [];

    for (const raw of nodes) {
      if (!isObject(raw)) {
        dropped += 1;
        continue;
      }

      const id = asId(raw.id);
      if (id == null || seen.has(id)) {
        dropped += 1 + countSubtree(raw.children);
        continue;
      }

      const status = asStatus(raw.status);
      if (status == null) {
        dropped += 1 + countSubtree(raw.children);
        continue;
      }

      const parentId = asId(raw.parentId);
      if (raw.parentId != null && parentId == null) {
        dropped += 1 + countSubtree(raw.children);
        continue;
      }

      seen.add(id);

      const children = Array.isArray(raw.children)
        ? walk(raw.children, depth + 1)
        : [];

      result.push({
        id,
        slug: asString(raw.slug),
        name: asString(raw.name),
        order: asNumber(raw.order),
        image: typeof raw.image === "string" ? raw.image : undefined,
        icon: typeof raw.icon === "string" ? raw.icon : undefined,
        showcase: asBoolean(raw.showcase, true),
        searchable: asBoolean(raw.searchable, true),
        filterable: asBoolean(raw.filterable, true),
        minisite: asBoolean(raw.minisite, false),
        navigation: asBoolean(raw.navigation, false),
        status,
        parentId: parentId ?? undefined,
        display_name: asLocaleMap(raw.display_name),
        description: asLocaleMap(raw.description),
        children,
        filters: typeof raw.filters === "string" ? raw.filters : null,
      });
    }

    return result;
  };

  return { categories: walk(input, 0), valid: true, dropped };
};
